import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import config from "@/config";
import { PRICEME_SITE_NAME } from "@/constants/site-seo.constants";

interface RealEstateListingSchemaProps {
    name: string;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    price: number;
}

interface PageHeadProps {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    titleOnly?: boolean;
    noIndex?: boolean;
    realEstateListing?: RealEstateListingSchemaProps;
}

/**
 * Component for managing document head metadata including canonical URLs
 */
const PageHead = ({
    title = "PriceMe",
    description = "Guess the price of real estate properties",
    canonicalUrl,
    titleOnly = false,
    noIndex = false,
    realEstateListing,
}: PageHeadProps) => {
    const location = useLocation();
    const baseUrl = config.BASE_URL || window.location.origin;
    const canonical = canonicalUrl || `${baseUrl}${location.pathname}`;

    // Ensure title/description are applied even if react-helmet is flaky in React 19.
    useEffect(() => {
        const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
            const selector = `meta[${attr}="${key}"]`;
            let el = document.head.querySelector(selector) as HTMLMetaElement | null;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        if (title) {
            document.title = title;
        }

        const descSelector = 'meta[name="description"]';
        const existingDesc = document.head.querySelector(descSelector) as HTMLMetaElement | null;

        if (titleOnly) {
            // For titleOnly pages, explicitly clear any stale description
            if (existingDesc) {
                existingDesc.setAttribute("content", "");
            }
        } else {
            upsertMeta("name", "description", description || "");
        }
    }, [title, description, titleOnly]);

    // Manually inject RealEstateListing JSON-LD
    useEffect(() => {
        if (!realEstateListing) {
            // Remove existing JSON-LD script if realEstateListing is removed
            const existingScript = document.querySelector(
                'script[type="application/ld+json"][data-priceme-jsonld="listing"]',
            );
            if (existingScript) {
                existingScript.remove();
            }
            return;
        }

        // Remove any existing JSON-LD script we created
        const existingScript = document.querySelector(
            'script[type="application/ld+json"][data-priceme-jsonld="listing"]',
        );
        if (existingScript) {
            existingScript.remove();
        }

        // Create and inject the JSON-LD script
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-priceme-jsonld", "listing");
        script.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateListing",
            name: realEstateListing.name,
            address: {
                "@type": "PostalAddress",
                streetAddress: realEstateListing.streetAddress,
                addressLocality: realEstateListing.addressLocality,
                addressRegion: realEstateListing.addressRegion,
                postalCode: realEstateListing.postalCode,
            },
            offers: {
                "@type": "Offer",
                price: realEstateListing.price,
                priceCurrency: "USD",
            },
        });

        document.head.appendChild(script);

        // Cleanup function
        return () => {
            const scriptToRemove = document.querySelector(
                'script[type="application/ld+json"][data-priceme-jsonld="listing"]',
            );
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        realEstateListing?.name,
        realEstateListing?.streetAddress,
        realEstateListing?.addressLocality,
        realEstateListing?.addressRegion,
        realEstateListing?.postalCode,
        realEstateListing?.price,
    ]);

    // If only the title is needed, return early
    if (titleOnly) {
        return (
            <Helmet>
                {noIndex && <meta name="robots" content="noindex, nofollow, noarchive" data-react-helmet="true" />}
                <title>{title}</title>
            </Helmet>
        );
    }

    return (
        <Helmet>
            {noIndex && <meta name="robots" content="noindex, nofollow, noarchive" data-react-helmet="true" />}
            <title>{title}</title>
            <meta name="description" content={description} data-react-helmet="true" />
            <meta property="og:site_name" content={PRICEME_SITE_NAME} data-react-helmet="true" />
            <link rel="canonical" href={canonical} data-react-helmet="true" />
            <meta property="og:url" content={canonical} data-react-helmet="true" />
        </Helmet>
    );
};

export default PageHead;
